import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { useLanguage } from "../../contexts/LanguageContext"; // 🌍 Translations Support
import ForgotPassword from "./ForgotPassword";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import createAuthService from "../../utils/AuthService";
import { useAppContext } from "../../contexts/AppContext";

// Styled Card component
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow: theme.shadows[1],
  ...theme.applyStyles("dark", {
    boxShadow: theme.shadows[1],
  }),
}));

// Main Container
const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(circle at top left, rgba(124, 58, 237, 0.18), transparent 32%), radial-gradient(circle at bottom right, rgba(79, 70, 229, 0.14), transparent 30%), linear-gradient(180deg, rgba(245, 243, 255, 0.78), rgba(255, 255, 255, 1))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(circle at top left, rgba(124, 58, 237, 0.28), transparent 28%), radial-gradient(circle at bottom right, rgba(79, 70, 229, 0.24), transparent 30%), linear-gradient(180deg, rgba(20, 16, 31, 1), rgba(28, 23, 48, 1))",
    }),
  },
}));

export default function SignIn() {
  const { setLanguage, translations } = useLanguage();
  const [data, setData] = React.useState({ email: "", password: "" });
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { updateAppState } = useAppContext();
  const authService = createAuthService();

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    const backendResponse = await authService.googleLoginAsync(credentialResponse.credential);
    
    updateAppState({
      isAuthenticated: true,
      accessToken: backendResponse.data.accessToken,
      refreshToken: backendResponse.data.refreshToken,
      success: `Welcome`,
    });

    var meResponse = await authService.meAsync(backendResponse.data.accessToken);
    if (meResponse.status !== 200) return;

    updateAppState({ user: meResponse.data });

    // set language context
    setLanguage(meResponse.data.language);

    if (!meResponse.data.associatedStoreIds?.length) {
      navigate("/store-creation");
      return;
    }

    navigate("/home");
  };

  const handleGoogleLoginError = () => {
    updateAppState({ error: "Google login failed" });
  };

  const handleStandardLogin = async () => {
    if (!data.email || !data.password) {
      setEmailError(!data.email);
      setEmailErrorMessage(!data.email ? translations.auth.email_required : "");
      setPasswordError(!data.password);
      setPasswordErrorMessage(!data.password ? translations.auth.password_required : "");
      return;
    }

    const backendResponse = await authService.emailLoginAsync(data.email, data.password);
    if (backendResponse.status !== 200) return;

    updateAppState({
      isAuthenticated: true,
      accessToken: backendResponse.data.accessToken,
      refreshToken: backendResponse.data.refreshToken,
    });

    var meResponse = await authService.meAsync(backendResponse.data.accessToken);
    if (meResponse?.status !== 200) return;

    updateAppState({ user: meResponse.data });
    
    setLanguage(meResponse.data.language);

    if (!meResponse.data.associatedStoreIds?.length) {
      navigate("/store-creation");
      return;
    }

    navigate("/home");
  };

  return (
    <SignInContainer direction="column" justifyContent="space-between">
      <Card variant="outlined">
        <Typography component="h1" variant="h4">
          {translations.auth.signin}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl>
            <FormLabel>{translations.auth.email}</FormLabel>
            <TextField
              onChange={(e) => setData({ ...data, email: e.target.value })}
              error={emailError}
              helperText={emailErrorMessage}
              id="email"
              type="email"
              required
              fullWidth
              variant="outlined"
            />
          </FormControl>

          <FormControl>
            <FormLabel>{translations.auth.password}</FormLabel>
            <TextField
              onChange={(e) => setData({ ...data, password: e.target.value })}
              error={passwordError}
              helperText={passwordErrorMessage}
              type="password"
              required
              fullWidth
              variant="outlined"
            />
          </FormControl>

          <ForgotPassword open={open} handleClose={() => setOpen(false)} />
          
          <Button fullWidth variant="contained" onClick={handleStandardLogin}>
            {translations.auth.signin}
          </Button>

          <Link onClick={() => setOpen(true)} variant="body2" sx={{ alignSelf: "center" }}>
            {translations.auth.forgot_password}
          </Link>
        </Box>

        <Divider>or</Divider>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ alignItems: "center", justifyContent: "center", justifySelf: "center", alignSelf: "center" }}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleLoginError}
              theme="outline"
              logo_alignment="left"
              shape="rectangular"
              size="large"
            />
          </Box>

          <Typography sx={{ textAlign: "center" }}>
            {translations.auth.dont_have_account}{" "}
            <Link href="/signup" variant="body2" sx={{ alignSelf: "center" }}>
              {translations.auth.signup}
            </Link>
          </Typography>
        </Box>
      </Card>
    </SignInContainer>
  );
}
